"use client"

import { useEffect, useMemo, useState } from "react"
import DesignAdventureApp from "./App"
import LessonOverview from "./LessonOverview"
import { getAvailableGames } from "./ContentLoader"
import { usePasswordCheck } from "./hooks/usePasswordCheck"
import { PasswordPrompt } from "./components/PasswordPrompt"
import "./css/AboutPage.css"

import {

  Crown,
 
} from "lucide-react"

const isComingSoon = (g) => Boolean(g?.comingSoon)

export default function LessonLauncher({
  languageCode = "nl",
  beta = false,

  // UI behavior
  variant = "lessons", // "lessons" | "landing"
  limit = null, // e.g. 6 on landing
  enableDeepLink = variant === "lessons",

  // Wrapper for non-play states (grid/preview)
  // signature: (children, mode) => JSX
  renderShell = null,
}) {
  const { isLessonUnlocked } = usePasswordCheck()

  const [startSectionIndex, setStartSectionIndex] = useState(null)


  // Modes: we REMOVE "password" mode entirely.
  const [mode, setMode] = useState("grid") // "grid" | "preview" | "play"
  const [activeGameId, setActiveGameId] = useState(null)

  // Overlay prompt (same banner you use inside lessons)
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false)

  // Game data
  const [games, setGames] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const getGameById = (id) => games.find((g) => g.id === id)
  

  const render = (children, currentMode) => {
    // ✅ Critical: when playing, ALWAYS full-screen (no shell/header/footer)
    if (currentMode === "play") return children
    return renderShell ? renderShell(children, currentMode) : children
  }

  // Deep-link support (only for /lessons)
useEffect(() => {
  if (!enableDeepLink) return

  const raw = window.location.hash.replace("#", "")
  const saved = localStorage.getItem("selectedGame")

  const startValue = raw || saved
  if (!startValue) return

  const [gameId, sectionStr] = startValue.split("@")
  const sectionIndex = Number.isFinite(Number(sectionStr)) ? Number(sectionStr) : null

  setActiveGameId(gameId)
  setStartSectionIndex(sectionIndex)

  const game = getGameById(gameId)
  if (game?.locked && !isLessonUnlocked(gameId)) {
    setMode("preview")
  } else {
    setMode("play")
  }
}, [enableDeepLink, games])


  // Load games list
  useEffect(() => {
    let alive = true

    const load = async () => {
      setIsLoading(true)
      try {
const available = await getAvailableGames("nl", beta)
        if (!alive) return
        setGames(Array.isArray(available) ? available : [])
      } catch (e) {
        console.error("Error loading games:", e)
        if (!alive) return
        setGames([])
      } finally {
        if (!alive) return
        setIsLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [languageCode, beta])

  // Keep hash/localStorage synced ONLY when playing (and only if enableDeepLink)
useEffect(() => {
  if (!enableDeepLink) return

  if (mode === "play" && activeGameId) {
    const suffix = Number.isFinite(startSectionIndex) ? `@${startSectionIndex}` : ""
    localStorage.setItem("selectedGame", activeGameId)
    window.location.hash = `${activeGameId}${suffix}`
  } else {
    localStorage.removeItem("selectedGame")
    window.location.hash = ""
  }
}, [enableDeepLink, mode, activeGameId, startSectionIndex])



  // Close unlock prompt on ESC
  useEffect(() => {
    if (!showUnlockPrompt) return
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowUnlockPrompt(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [showUnlockPrompt])

  const sortedGames = useMemo(() => {
    return [...games].sort((a, b) => {
      // Coming soon last
      if (a.comingSoon && !b.comingSoon) return 1
      if (!a.comingSoon && b.comingSoon) return -1
      // Featured first
      if (a.featured && !b.featured) return -1
      if (!a.featured && b.featured) return 1
      return 0
    })
  }, [games])

// Scroll to top ONLY when the overview opens
useEffect(() => {
  if (mode !== "preview" || !activeGameId) return

  // Wait until the preview UI is painted (more reliable with SPA navigation)
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, behavior: "auto" })
  })
}, [mode, activeGameId])



  const visibleGames = useMemo(() => {
    if (!limit) return sortedGames
    return sortedGames.slice(0, limit)
  }, [sortedGames, limit])

  const openPreview = (gameId) => {
    const game = getGameById(gameId)
    if (!game) return

    // ✅ Always allow preview/overview, even if locked
    setActiveGameId(gameId)
    setMode("preview")
  }

  const startLesson = () => {
    if (!activeGameId) return
    const game = getGameById(activeGameId)
    if (!game) return

    // ✅ If locked, show the SAME banner overlay and keep the page visible behind it
    if (game?.locked && !isLessonUnlocked(activeGameId)) {
      setShowUnlockPrompt(true)
      return
    }

    setMode("play")
  }

  // Optional: Start from section (if you wire this later from LessonOverview)
const startLessonAtSection = (sectionIndex) => {
  if (!activeGameId) return

  const game = getGameById(activeGameId)
  if (!game) return

  if (game?.locked && !isLessonUnlocked(activeGameId)) {
    setShowUnlockPrompt(true)
    return
  }

  setStartSectionIndex(sectionIndex)
  setMode("play")
}


  const backToGrid = () => {
    setMode("grid")
    setActiveGameId(null)
    setShowUnlockPrompt(false)
  }

  // ----- PREVIEW (overview) -----
  if (mode === "preview" && activeGameId) {
    const game = getGameById(activeGameId)
    const locked = Boolean(game?.locked && !isLessonUnlocked(activeGameId))

    return render(
      <div style={{ position: "relative" }}>
        <LessonOverview
  languageCode="nl"
          beta={beta}
          gameId={activeGameId}
          onBack={backToGrid}
          onStart={startLesson}
          onStartAtSection={startLessonAtSection}
          isLessonLocked={locked}
        />

        {/* ✅ Overlay banner prompt (page stays visible behind it) */}
        {showUnlockPrompt && locked && (
          <div
            onClick={() => setShowUnlockPrompt(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0,0,0,0.65)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(740px, 100%)",
                position: "relative",
              }}
            >
              <button
                onClick={() => setShowUnlockPrompt(false)}
                aria-label="Sluiten"
                style={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "1px solid var(--color-border)",
                  background: "white",
                  cursor: "pointer",
                  fontWeight: 800,
                  boxShadow: "var(--shadow-md)",
                }}
              >
                ✕
              </button>

              {/* This is your existing banner component that starts with:
                  "Ontgrendel Direct Alle Huidige en Nieuwe Lessen!" */}
              <PasswordPrompt lessonTitle={game?.title || "Les"} />
            </div>
          </div>
        )}
      </div>,
      mode,
    )
  }

  // ----- PLAY (full-screen) -----
 if (mode === "play" && activeGameId) {
  return render(
    <DesignAdventureApp
      gameId={activeGameId}
      beta={beta}
        languageCode="nl"

      initialSectionIndex={startSectionIndex}
      onReturnHome={() => {
        // Go back to overview instead of grid
        setMode("preview")
        setStartSectionIndex(null)
      }}
    />,
    mode,
  )
}


  // ----- GRID -----
  return render(
  <div className="games-content">
    {isLoading ? (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    ) : visibleGames.length === 0 ? (
      <div className="no-games-container">
        <div className="no-games-emoji">🔍</div>
        <h2 className="no-games-title">Geen lessen gevonden</h2>
        <p className="no-games-text">Probeer later opnieuw.</p>
      </div>
    ) : (
      <>
        <div className="games-grid">
          {visibleGames.map((game) =>
            isComingSoon(game) ? (
              <div
                key={game.id}
                className={`game-card ${game.bgColor} coming-soon-card`}
                aria-disabled="true"
              >
                <div className="comingsoon-badge">{game.comingSoonText || "Coming soon"}</div>
                <div className="game-card-header">
                  <div className="game-info">
                    <h2 className="game-title">{game.title}</h2>
                    <p className="game-description">{game.description}</p>
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={game.id}
                onClick={() => openPreview(game.id)}
                className={`game-card ${game.bgColor} ${game.featured ? "featured" : ""} ${
                  game.locked && !isLessonUnlocked(game.id) ? "locked-lesson" : ""
                }`}
              >
                {game.featured && <div className="featured-badge">{game.featuredText || "Uitgelicht"}</div>}
                {game.locked && !isLessonUnlocked(game.id) && (
                  <div className="locked-badge"><div className="premium-badge">
  <Crown size={14} />
  Premium
</div></div>
                )}

                <div className="game-emoji">
                  {game.iconImage ? (
                    <img src={game.iconImage} alt="" className="image object-contain" />
                  ) : (
                    <span className="emoji text-4xl">{game.emoji || "📱"}</span>
                  )}
                </div>

                <div className="game-card-header">
                  <div className="game-info">
                    <h2 className="game-title">{game.title}</h2>
                    <div className="game-meta">
                      <span className="game-meta-pill">{game.estimatedDuration}</span>
                    </div>
                    <p className="game-description">{game.description}</p>
                  </div>
                </div>
              </button>
            ),
          )}
        </div>

      
      </>
    )}
  </div>,
  mode,
)

}
